import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import _ from 'lodash';
import styled from 'styled-components';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import {
  MdEdit, MdDelete, MdClose, MdFilterList, MdArrowBack,
} from 'react-icons/lib/md';

import { API_BASE_URL } from '../../styles/constant';
import { getAmendmentsForBill, getBillTrackWithId } from '../../services/billTrackServices';
import blockProps from '../../utils/blockProps';
import Auth from '../../components/Auth';
import AppShell from '../../components/AppShell';
import Loader from '../../components/Loader';
import DataTable from '../../components/DataTable';
import ModalForm from '../../components/ModalForm';
import ModalFilter from '../../components/ModalFilter';
import ModalConfirmation from '../../components/ModalConfirmation';
// import SessionVisualization from '../../components/SessionVisualization';

class BillDetail extends React.Component {
  static propTypes = {
    authenticatedUserInfo: PropTypes.object.isRequired,
    match: PropTypes.shape({
      params: PropTypes.object.isRequired,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    dataLoaded: false,
    billDetails: {},
    amendments: [],
    headers: [],
    error: null,
    loading: false,
    snackbarOpened: false,
    snackbarMessage: '',
    serverError: false,
    filterConfig: {},
  };

  dataTableRef = null;

  unsubscribe = null;

  componentDidMount() {
    const { billID } = this.props.match.params;

    this.unsubscribe = getAmendmentsForBill(billID, (amendments) => {
      this.setState({ amendments });
    });
    this.getBillDetail();
    this.fetchDatatableHeaders();
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  getBillDetail = async () => {
    const { billID } = this.props.match.params;
    try {
      const { data } = await getBillTrackWithId(billID);
      this.setState({ billDetails: data });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  fetchDatatableHeaders = async () => {
    try {
      const {
        data: { data },
      } = await axios.options(`${API_BASE_URL}/billTrack/amendment/datatable`);
      this.setState({ dataLoaded: true, headers: data || [] });
    } catch (error) {
      this.setState({ dataLoaded: true, error: error.message });
    }
  };

  getFilteredAmendments = () => {
    const { amendments } = this.state;

    let filteredAmendments = amendments;
    const filterKeys = Object.keys(this.state.filterConfig);
    if (filterKeys.length > 0) {
      filteredAmendments = _.filter(amendments, amendmentData => filterKeys
        .map((filterKey) => {
          const filterData = this.state.filterConfig[filterKey];
          if (filterData.lookupType === 'equal') {
            if (Array.isArray(filterData.value)) {
              return filterData.value.includes(_.get(amendmentData, filterKey));
            }
            return _.get(amendmentData, filterKey) === filterData.value;
          }
          if (filterData.lookupType === 'geq') {
            return _.get(amendmentData, filterKey) >= filterData.value;
          }
          if (filterData.lookupType === 'leq') {
            return _.get(amendmentData, filterKey) <= filterData.value;
          }
          return false;
        })
        .every(val => val));
    }

    return filteredAmendments;
  };

  deleteAmendment = async (amendmentID, callback) => {
    this.setState({ loading: true });
    const { billID } = this.props.match.params;

    try {
      const { data } = await axios.delete(
        `${API_BASE_URL}/billTrack/removeAmendment/${billID}/${amendmentID}`,
      );
      const response = data.data || {};

      this.setState(
        {
          loading: false,
          snackbarOpened: true,
          snackbarMessage: response.message,
        },
        () => {
          if (this.dataTableRef) {
            this.dataTableRef.resetSelectedRows();
          }
          callback();
        },
      );
    } catch (error) {
      const errorMessages = _.get(error, 'response.data.error');
      this.setState({
        loading: false,
        serverError: true,
        snackbarOpened: true,
        snackbarMessage: errorMessages || 'something went wrong',
      });
    }
  };

  handleSnackbarClose = () => {
    this.setState({
      snackbarOpened: false,
      snackbarMessage: '',
      serverError: false,
    });
  };

  renderToolbars = (selectedRows) => {
    const { billID } = this.props.match.params;

    if (selectedRows.length === 0) {
      return (
        <ToolbarContainer>
          <ModalFilter
            apiEndpoint={`${API_BASE_URL}/billTrack/amendment/filters`}
            trigger={(
              <IconButton aria-label="Filter list">
                <MdFilterList />
              </IconButton>
)}
            tooltipTitle="Filter"
            onApply={(filterConfig) => {
              this.setState({ filterConfig });
            }}
            onClear={() => {
              this.setState({ filterConfig: {} });
            }}
            initialValues={this.state.filterConfig}
          />
          <ModalForm
            key="create-amendment"
            tooltipTitle="Create Amendment"
            trigger={(
              <Button variant="contained" color="primary">
                Create Amendment
              </Button>
)}
            apiEndpoint={`${API_BASE_URL}/billTrack/createAmendment/${billID}`}
          />
        </ToolbarContainer>
      );
    }

    return (
      <ToolbarContainer>
        {selectedRows.length === 1 && [
          <ModalForm
            key="edit-amendment"
            tooltipTitle="Edit Amendment"
            trigger={(
              <IconButton aria-label="Edit Amendment">
                <MdEdit size={24} />
              </IconButton>
)}
            apiEndpoint={`${API_BASE_URL}/billTrack/updateAmendment/${billID}/${selectedRows[0]}/`}
            method="put"
            onClose={() => {
              if (this.dataTableRef) {
                this.dataTableRef.resetSelectedRows();
              }
            }}
          />,
          <ModalConfirmation
            key="delete-bill"
            trigger={(
              <IconButton aria-label="Delete">
                <MdDelete />
              </IconButton>
)}
            onConfirm={({ closeModal }) => {
              this.deleteAmendment(selectedRows[0], closeModal);
            }}
          />,
        ]}
      </ToolbarContainer>
    );
  };

  render() {
    return (
      <div>
        <AppShell>
          {this.state.error ? (
            <Typography variant="subheading" color="error">
              {this.state.error}
            </Typography>
          ) : !this.state.dataLoaded ? (
            <Loader />
          ) : (
            <div>
              <BackButton
                variant="outlined"
                color="secondary"
                onClick={() => {
                  this.props.history.push('/bill-track');
                }}
              >
                <MdArrowBack size={20} />
                Back To Bill Track
              </BackButton>

              <StyledCard>
                <Row>
                  <Row>
                    <Label>Bill Name</Label>
                    <Text>{_.get(this.state.billDetails, 'billName')}</Text>
                  </Row>
                  <Row>
                    <Label>Session</Label>
                    <Text>{_.get(this.state.billDetails, 'session')}</Text>
                  </Row>
                  <Row>
                    <Label>JYP City</Label>
                    <Text>{_.get(this.state.billDetails, 'jypCity')}</Text>
                  </Row>
                  <Row>
                    <Label>Bill Discussion Time</Label>
                    <Text>{`${_.get(this.state.billDetails, 'billDiscussionTime')} minutes`}</Text>
                  </Row>
                  <Row>
                    <Label>Attendance (Govt)</Label>
                    <Text>{_.get(this.state.billDetails, 'attendanceGovernment', 0)}</Text>
                  </Row>
                  <Row>
                    <Label>Attendance (Oppo)</Label>
                    <Text>{_.get(this.state.billDetails, 'attendanceOpposition', 0)}</Text>
                  </Row>
                  <Row>
                    <Label>Votes (Favour)</Label>
                    <Text>{_.get(this.state.billDetails, 'votesFavour', 0)}</Text>
                  </Row>
                  <Row>
                    <Label>Votes (Against)</Label>
                    <Text>{_.get(this.state.billDetails, 'votesAgainst', 0)}</Text>
                  </Row>
                  <Row>
                    <Label>NOTA</Label>
                    <Text>{_.get(this.state.billDetails, 'votesNota', 0)}</Text>
                  </Row>
                  <Row>
                    <Label>Bill Status</Label>
                    <Text>{_.get(this.state.billDetails, 'billStatus', 'N/A')}</Text>
                  </Row>
                </Row>

                <ActionsContainer>
                  <ModalForm
                    key="edit-bill"
                    tooltipTitle="Edit Bill"
                    trigger={(
                      <Button variant="contained" color="primary">
                        <MdEdit size={20} />
                        Update
                      </Button>
)}
                    apiEndpoint={`${API_BASE_URL}/billTrack/updateBill/${
                      this.props.match.params.billID
                    }/?userType=${this.props.authenticatedUserInfo.type}`}
                    method="put"
                    onSuccess={this.getBillDetail}
                  />
                </ActionsContainer>
              </StyledCard>

              <DataTable
                ref={(comp) => {
                  this.dataTableRef = comp;
                }}
                title="Amendments"
                tableHeaders={this.state.headers}
                tableData={this.getFilteredAmendments()}
                renderToolbars={selectedRows => this.renderToolbars(selectedRows)}
              />
            </div>
          )}
        </AppShell>

        <Snackbar
          open={this.state.snackbarOpened}
          autoHideDuration={3000}
          onClose={this.handleSnackbarClose}
        >
          <StyledSnackbarContent
            serverError={this.state.serverError}
            message={this.state.snackbarMessage}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={this.handleSnackbarClose}
              >
                <MdClose size={20} />
              </IconButton>,
            ]}
          />
        </Snackbar>

        {this.state.loading && <Loader />}
      </div>
    );
  }
}

export default props => <Auth>{({ authState }) => <BillDetail {...props} {...authState} />}</Auth>;

const BackButton = styled(Button)`
  && {
    margin-bottom: 30px;
    border: none;
    outline: none;
  }
`;

const StyledCard = styled(Card)`
  padding: 16px;
  margin-bottom: 24px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
`;

const Label = styled(Typography).attrs({
  variant: 'body2',
})`
  && {
    color: ${props => props.theme.palette.primary.dark};
  }
`;

const Text = styled(Typography).attrs({
  variant: 'body1',
})``;

const ActionsContainer = styled.div`
  && {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    & > * + * {
      margin-left: 16px;
    }
  }
`;

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 10px !important;
  }
`;

const StyledSnackbarContent = styled(blockProps(SnackbarContent, ['serverError']))`
  background-color: ${props => (props.serverError ? 'red' : 'green')} !important;
`;



// WEBPACK FOOTER //
// ./src/pages/BillDetail/BillDetail.js